import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Form, Button, Row, Col, Spinner, Alert, Badge, Modal } from 'react-bootstrap';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { fmtDate } from '../lib/lookups';

/**
 * CountyMapPanel
 * ----------------------------------------------------------------------------
 * Admin UI for generating/managing a state's interactive county-map SVG.
 *
 * Workflow:
 *   1. Admin enters a GeoJSON URL (typically US Census county GeoJSON)
 *   2. Click "Generate / Rebuild SVG" -> backend fetches GeoJSON, filters
 *      to this state's FIPS, projects every county polygon, and emits a
 *      sanitized SVG. Result is persisted on the state record.
 *   3. Preview pane renders the SVG inline; status block shows match
 *      summary (matched / unmatched features / unmatched DB counties).
 *
 * State-agnostic by design — it consumes whatever the state's existing
 * FIPS, slug, and county records provide.
 */
const DEFAULT_GEOJSON_URL = 'https://eric.clst.org/assets/wiki/uploads/Stuff/gz_2010_us_050_00_20m.json';

export default function CountyMapPanel({ stateId, stateAbbreviation, stateFips }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [existing, setExisting] = useState(null);
  const [geojsonUrl, setGeojsonUrl] = useState('');
  const [source, setSource] = useState('');
  const [urlTemplate, setUrlTemplate] = useState('/local-elections/{slug}');
  const [working, setWorking] = useState(false);
  const [busy, setBusy] = useState('');
  const [previewSvg, setPreviewSvg] = useState('');
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [showClear, setShowClear] = useState(false);
  // Floating hover label for the preview pane. Tracks the cursor and shows
  // the county the user is currently over. Browsers also show the native
  // <title> tooltip after ~1s, but this gives instant feedback in-admin.
  const previewWrapRef = useRef(null);
  const [hover, setHover] = useState(null); // { name, active, x, y }

  // Wire mouse listeners onto the SVG after every (re)render of previewSvg.
  // We delegate at the wrapper so each new SVG load picks up the handlers
  // without re-binding per path.
  useEffect(() => {
    const wrap = previewWrapRef.current;
    if (!wrap) return undefined;

    function findCountyPath(el) {
      // Walk up from the event target until we find a .nvg-county <path>.
      let n = el;
      while (n && n !== wrap) {
        if (n.tagName === 'path' && n.classList && n.classList.contains('nvg-county')) return n;
        n = n.parentNode;
      }
      return null;
    }

    function onMove(e) {
      const p = findCountyPath(e.target);
      if (!p) { setHover(null); return; }
      const wrapRect = wrap.getBoundingClientRect();
      setHover({
        name: p.getAttribute('data-name') || p.getAttribute('data-slug') || '(unknown)',
        active: p.classList.contains('is-active'),
        x: e.clientX - wrapRect.left,
        y: e.clientY - wrapRect.top,
      });
    }
    function onLeave() { setHover(null); }

    wrap.addEventListener('mousemove', onMove);
    wrap.addEventListener('mouseleave', onLeave);
    return () => {
      wrap.removeEventListener('mousemove', onMove);
      wrap.removeEventListener('mouseleave', onLeave);
    };
  }, [previewSvg]);

  const fipsHint = useMemo(() => {
    if (stateFips && /^\d{1,2}$/.test(stateFips)) return stateFips.padStart(2, '0');
    // Quick visual hint only — backend authoritatively resolves via abbreviation lookup
    const guess = { FL: '12', CA: '06', TX: '48', NY: '36', GA: '13' }[(stateAbbreviation || '').toUpperCase()];
    return guess || '';
  }, [stateFips, stateAbbreviation]);

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.get(`/admin/states/${stateId}/county-map`);
      setExisting(res.data);
      if (res.data?.geojson_url && !geojsonUrl) setGeojsonUrl(res.data.geojson_url);
      if (res.data?.source && !source) setSource(res.data.source);
      if (res.data?.svg) setPreviewSvg(res.data.svg);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!stateId) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateId]);

  function validateInputs() {
    if (!geojsonUrl?.trim()) {
      setError('Provide a GeoJSON URL (e.g., US Census 2010 county boundaries).');
      return false;
    }
    if (!/^https?:\/\//i.test(geojsonUrl.trim())) {
      setError('GeoJSON URL must start with http(s)://');
      return false;
    }
    if (!urlTemplate.includes('{slug}')) {
      setError('URL template must contain `{slug}`.');
      return false;
    }
    setError('');
    return true;
  }

  async function runBuild(persist) {
    if (!validateInputs()) return;
    setWorking(true);
    setBusy(persist ? 'generate' : 'preview');
    setSummary(null);
    try {
      const body = {
        geojson_url: geojsonUrl.trim(),
        county_url_template: urlTemplate.trim(),
        source: source.trim(),
      };
      const path = persist ? 'generate' : 'preview';
      const res = await adminApi.post(`/admin/states/${stateId}/county-map/${path}`, body);
      const d = res.data;
      setPreviewSvg(d.svg || '');
      setSummary({
        matched: d.matched,
        feature_count: d.feature_count,
        unmatched_features: d.unmatched_features || [],
        unmatched_counties: d.unmatched_counties || [],
        state_fips: d.state_fips,
        county_url_template: d.county_url_template,
      });
      if (persist) {
        toast.success(`Map saved — ${d.matched}/${d.feature_count} counties matched`);
        await refresh();
      } else {
        toast.success(`Preview ready — ${d.matched}/${d.feature_count} counties matched`);
      }
    } catch (e) {
      const msg = extractError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setWorking(false);
      setBusy('');
    }
  }

  async function clearMap() {
    setBusy('clear');
    try {
      await adminApi.delete(`/admin/states/${stateId}/county-map`);
      toast.success('Stored county map cleared');
      setPreviewSvg('');
      setSummary(null);
      setShowClear(false);
      await refresh();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy('');
    }
  }

  const hasStored = !!existing?.has_svg;

  return (
    <Card className="mt-4" data-testid="county-map-panel">
      <Card.Header className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-map" />
          <strong>County Map SVG</strong>
          {hasStored ? (
            <Badge bg="success" className="ms-1">Saved</Badge>
          ) : (
            <Badge bg="secondary" className="ms-1">Not generated</Badge>
          )}
        </div>
        <div className="d-flex gap-2">
          {hasStored && (
            <Button size="sm" variant="outline-danger" onClick={() => setShowClear(true)} disabled={!!busy} data-testid="county-map-clear-button">
              <i className="bi bi-trash me-1" /> Clear
            </Button>
          )}
        </div>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center p-3"><Spinner animation="border" size="sm" /></div>
        ) : (
          <>
            {error && <Alert variant="danger" data-testid="county-map-error">{error}</Alert>}
            {!fipsHint && !stateFips && (
              <Alert variant="warning" style={{ fontSize: '0.85rem' }}>
                This state has no <code>state_fips</code> set. The backend will try to look it up from
                the abbreviation, but it&apos;s safer to set it explicitly on this state.
              </Alert>
            )}

            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>GeoJSON source URL</Form.Label>
                  <Form.Control
                    type="url"
                    value={geojsonUrl}
                    onChange={(e) => setGeojsonUrl(e.target.value)}
                    placeholder="https://… (US Census county GeoJSON)"
                    data-testid="county-map-geojson-url-input"
                  />
                  <Form.Text>
                    Counties are matched to this state by FIPS{fipsHint && <> (<code>{fipsHint}</code>)</>}.
                    {' '}
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline"
                      onClick={() => setGeojsonUrl(DEFAULT_GEOJSON_URL)}
                      data-testid="county-map-use-default-url"
                    >
                      Use US Census 2010 default
                    </button>
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Attribution / source name</Form.Label>
                  <Form.Control
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    placeholder='e.g., "US Census 2010 cb_500k"'
                    data-testid="county-map-source-input"
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>County URL template</Form.Label>
                  <Form.Control
                    value={urlTemplate}
                    onChange={(e) => setUrlTemplate(e.target.value)}
                    placeholder="/local-elections/{slug}"
                    data-testid="county-map-url-template-input"
                  />
                  <Form.Text>Must contain <code>{'{slug}'}</code>. Emitted as <code>data-url</code> on each county path.</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex gap-2 mt-3">
              <Button
                variant="outline-secondary"
                onClick={() => runBuild(false)}
                disabled={working}
                data-testid="county-map-preview-button"
              >
                {busy === 'preview' ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-eye me-1" /> Preview</>}
              </Button>
              <Button
                variant="primary"
                onClick={() => runBuild(true)}
                disabled={working}
                data-testid="county-map-generate-button"
              >
                {busy === 'generate' ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-magic me-1" /> Generate / Rebuild SVG</>}
              </Button>
            </div>

            {(summary || hasStored) && (
              <div className="mt-3" data-testid="county-map-status">
                {summary && (
                  <div className="d-flex flex-wrap gap-2 mb-2" style={{ fontSize: '0.85rem' }}>
                    <Badge bg="success">{summary.matched} matched</Badge>
                    <Badge bg="light" text="dark">{summary.feature_count} GeoJSON features</Badge>
                    {summary.unmatched_features.length > 0 && (
                      <Badge bg="warning" text="dark" title={summary.unmatched_features.join(', ')}>
                        {summary.unmatched_features.length} unmatched feature(s)
                      </Badge>
                    )}
                    {summary.unmatched_counties.length > 0 && (
                      <Badge bg="warning" text="dark" title={summary.unmatched_counties.join(', ')}>
                        {summary.unmatched_counties.length} DB counties not in GeoJSON
                      </Badge>
                    )}
                    {summary.state_fips && (
                      <Badge bg="info">state FIPS: {summary.state_fips}</Badge>
                    )}
                  </div>
                )}
                {summary?.unmatched_features?.length > 0 && (
                  <Alert variant="warning" className="mb-2" style={{ fontSize: '0.82rem' }}>
                    <strong>Unmatched GeoJSON features</strong> (rendered as disabled): {' '}
                    {summary.unmatched_features.slice(0, 12).join(', ')}
                    {summary.unmatched_features.length > 12 && `, +${summary.unmatched_features.length - 12} more`}
                  </Alert>
                )}
                {summary?.unmatched_counties?.length > 0 && (
                  <Alert variant="warning" className="mb-2" style={{ fontSize: '0.82rem' }}>
                    <strong>DB counties not in GeoJSON</strong> (no path drawn): {' '}
                    {summary.unmatched_counties.slice(0, 12).join(', ')}
                    {summary.unmatched_counties.length > 12 && `, +${summary.unmatched_counties.length - 12} more`}
                  </Alert>
                )}
                {hasStored && (
                  <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                    Saved {existing.updated_at ? fmtDate(existing.updated_at) : '—'}
                    {existing.source ? <> · {existing.source}</> : null}
                    {existing.geojson_url ? <> · <a href={existing.geojson_url} target="_blank" rel="noreferrer">source URL</a></> : null}
                  </div>
                )}
              </div>
            )}

            {previewSvg ? (
              <div className="mt-3">
                <div className="text-muted mb-1" style={{ fontSize: '0.82rem' }}>
                  Preview <span className="text-muted" style={{ fontSize: '0.78rem' }}>· hover a county to see its name</span>
                </div>
                <div
                  className="border rounded p-2"
                  style={{ background: '#f8f9fa', maxHeight: 520, overflow: 'auto', position: 'relative' }}
                  data-testid="county-map-preview"
                  ref={previewWrapRef}
                >
                  {/* Generated server-side, sanitized at build time + on read; safe to inline. */}
                  <div
                    style={{ width: '100%', maxWidth: 720, margin: '0 auto' }}
                    dangerouslySetInnerHTML={{ __html: previewSvg }}
                  />
                  {hover && (
                    <div
                      data-testid="county-map-hover-label"
                      role="tooltip"
                      style={{
                        position: 'absolute',
                        left: Math.min(hover.x + 14, (previewWrapRef.current?.clientWidth || 9999) - 220),
                        top: Math.max(hover.y - 30, 6),
                        background: 'rgba(33, 37, 41, 0.92)',
                        color: '#fff',
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: '0.82rem',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                        zIndex: 5,
                      }}
                    >
                      <strong>{hover.name}</strong>
                      {!hover.active && <span style={{ opacity: 0.75 }}> · no data</span>}
                    </div>
                  )}
                </div>
                <style>{`
                  [data-testid="county-map-preview"] svg { width: 100%; height: auto; }
                  [data-testid="county-map-preview"] .nvg-county { fill: #cfe2ff; stroke: #ffffff; stroke-width: 0.6; transition: fill 120ms ease; }
                  [data-testid="county-map-preview"] .nvg-county.is-active:hover { fill: #0d6efd; cursor: pointer; }
                  [data-testid="county-map-preview"] .nvg-county.is-disabled { fill: #e9ecef; }
                  [data-testid="county-map-preview"] .nvg-county.is-disabled:hover { fill: #ced4da; cursor: not-allowed; }
                `}</style>
              </div>
            ) : (
              <div className="mt-3 text-muted" style={{ fontSize: '0.85rem' }}>
                No SVG yet. Generate one to see a preview here.
              </div>
            )}
          </>
        )}
      </Card.Body>

      <Modal show={showClear} onHide={() => setShowClear(false)}>
        <Modal.Header closeButton><Modal.Title>Clear county map?</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>This removes the stored SVG for this state. The public API will fall back to returning only the county list.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowClear(false)} disabled={!!busy}>Cancel</Button>
          <Button variant="danger" onClick={clearMap} disabled={!!busy} data-testid="county-map-clear-confirm-button">
            {busy === 'clear' ? <Spinner size="sm" animation="border" /> : 'Clear map'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
